import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import MovieFilterBar from '@/components/feature/MovieFilterBar';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { getCategory, filterMovies, getTagList, type SortKey } from '@/lib/queries';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

const sortLabels: Record<SortKey, string> = {
  latest: '最新收录',
  popular: '浏览量',
  rating: '评分',
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = getCategory(type ?? '');

  const [filters, setFilters] = useState({
    region: '全部',
    year: '全部',
    duration: '全部',
    sort: 'latest' as SortKey,
  });
  const [visible, setVisible] = useState(PAGE_SIZE);

  const result = useMemo(
    () =>
      filterMovies({
        category: category?.slug,
        region: filters.region,
        year: filters.year,
        duration: filters.duration,
        sort: filters.sort,
      }),
    [category?.slug, filters],
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [filters, type]);

  const tagList = getTagList().slice(0, 8);

  useSeo({
    title: category ? `${category.name} - 日韩成人电影` : '分类不存在 - 日韩成人电影',
    description: category
      ? `浏览日韩成人电影中的${category.name}分类和影片介绍，包含地区、年份与片长筛选，支持按最新、最热与评分排序。`
      : '该分类不存在，请返回首页或浏览其他影片分类。',
    keywords: category
      ? `${category.name},日韩成人电影,${category.name}影片,${category.name}推荐,成人电影分类`
      : '日韩成人电影,影片分类',
  });

  if (!category) {
    return (
      <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
        <EmptyState
          icon="ri-error-warning-line"
          title="没有找到这个分类"
          description="链接可能已经失效，或者分类编码有误。你可以返回首页，或直接进入分类列表重新浏览。"
          primaryLabel="返回首页"
          primaryTo="/"
        />
      </div>
    );
  }

  const shown = result.slice(0, visible);

  return (
    <>
      <PageHeader
        eyebrow={`Category · ${category.en}`}
        title={category.name}
        description={category.intro}
        crumbs={[{ label: '分类', to: '/category/drama' }, { label: category.name }]}
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-sm text-primary-300" />
              共 {result.length} 部影片
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-sort-desc text-sm text-primary-300" />
              当前排序：{sortLabels[filters.sort]}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-sm text-primary-300" />
              {category.description}
            </span>
          </>
        }
      />

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <MovieFilterBar
              value={filters}
              onChange={setFilters}
              onReset={() => setFilters({ region: '全部', year: '全部', duration: '全部', sort: 'latest' })}
              resultCount={result.length}
            />

            {shown.length === 0 ? (
              <div className="mt-8">
                <EmptyState
                  title="暂无符合条件的影片"
                  description="尝试重置筛选条件，或者换一个地区与年份组合再看看。也可以直接浏览其他分类。"
                  onReset={() => setFilters({ region: '全部', year: '全部', duration: '全部', sort: 'latest' })}
                  resetLabel="重置筛选"
                  primaryLabel="浏览全部分类"
                  primaryTo="/"
                />
              </div>
            ) : (
              <>
                <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
                  {shown.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                <div className="mt-10 flex flex-col items-center gap-3">
                  <p className="text-xs text-foreground-600">
                    已显示 {shown.length} / {result.length} 部影片
                  </p>
                  {visible < result.length && (
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                      className="cursor-pointer whitespace-nowrap rounded-md border border-background-700 px-6 py-2.5 text-sm text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
                    >
                      加载更多影片
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <aside className="w-full shrink-0 lg:w-[280px]">
            <div className="rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="text-sm font-semibold text-foreground-100">相关标签</h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                通过标签可以直接跳转到跨分类的影片合集。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {tagList.map((tag) => (
                  <Link
                    key={tag.name}
                    to={`/tag/${encodeURIComponent(tag.name)}`}
                    className="cursor-pointer rounded-full border border-background-700 px-2.5 py-1 text-[11px] text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    {tag.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="text-sm font-semibold text-foreground-100">观影提示</h2>
              <ul className="mt-3 space-y-2.5 text-xs leading-relaxed text-foreground-500">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-sm text-accent-500" />
                  片长超过 120 分钟的作品节奏偏慢，建议预留完整时间。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-sm text-accent-500" />
                  悬疑类作品不建议提前查看评论区，容易影响观感。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-sm text-accent-500" />
                  本站仅提供资料介绍，不提供播放与下载。
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}